import React from 'react'
import { Button, Input, InputNumber } from 'antd'
import styled from 'styled-components'
import {
  isInCreditWorkflow,
  isInvoiceReconciledAgainstPurchaseOrder,
  isReceipted,
} from 'src/utils/reconciliationHealth'
import { isReceiptNotSaved } from 'src/utils/goodsReceiptingStatusHelpers'

const Wrap = styled.div`
  margin: 0 0 12px;
  padding: 12px 14px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  max-width: 640px;
`

const Title = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.85);
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  align-items: flex-end;
  margin-bottom: 8px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 140px;
`

const Label = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`

const Breakdown = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 8px;
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

function formatPoundsFromPennies(pennies) {
  const value = Number(pennies)
  if (!Number.isFinite(value)) return '£0.00'
  return `£${(value / 100).toFixed(2)}`
}

export function canEditPoSupplierDiscount(record = {}, uploadedInvoices = []) {
  if (record?.isVoided) return false
  if (isInCreditWorkflow(record)) return false
  if (record?.receiptingStatus === 'CN Requested') return false
  if (!isReceipted(record)) return true
  return (
    record?.placedVia === 'Order placed manually on the dashboard' &&
    !isInvoiceReconciledAgainstPurchaseOrder(record, uploadedInvoices)
  )
}

export function isSupplierDiscountDraftDirty(record = {}, draft = {}) {
  const savedPennies = Math.max(
    0,
    Math.round(Number(record?.supplierDiscountPennies) || 0),
  )
  const savedNote = String(record?.supplierDiscountNote || '')
  const draftPennies = Math.max(
    0,
    Math.round(Number(draft?.discountPennies) || 0),
  )
  const draftNote = String(draft?.note || '')
  return draftPennies !== savedPennies || draftNote !== savedNote
}

/**
 * PO-level supplier discount / credit on Goods Receipting expanded row.
 */
export default function SupplierDiscountPanel({
  record,
  draft,
  onChange,
  onSave,
  saving = false,
  uploadedInvoices = [],
}) {
  const editable = canEditPoSupplierDiscount(record, uploadedInvoices)
  const invoiceReconciled = isInvoiceReconciledAgainstPurchaseOrder(
    record,
    uploadedInvoices,
  )
  const dirty = isSupplierDiscountDraftDirty(record, draft)
  const orderedPennies = Number(record?.totalSpendOrdered) || 0
  const linesReceivedPennies = Number(
    draft?.linesReceivedPennies ??
      record?.totalSpendReceivedBeforeDiscount ??
      record?.totalSpendReceived ??
      0,
  )
  const discountPennies = Math.max(
    0,
    Math.round(
      Number(draft?.discountPennies ?? record?.supplierDiscountPennies) || 0,
    ),
  )
  const receiptedTotalPennies = Math.max(
    0,
    linesReceivedPennies - discountPennies,
  )
  const note = draft?.note ?? record?.supplierDiscountNote ?? ''

  return (
    <Wrap>
      <Title>Supplier discount / credit</Title>
      <Row>
        <Field>
          <Label>Discount amount (£)</Label>
          <InputNumber
            min={0}
            step={0.01}
            precision={2}
            disabled={!editable}
            value={discountPennies / 100}
            onChange={value => {
              const pounds = value == null || value === '' ? 0 : Number(value)
              onChange?.({
                discountPennies: Math.max(
                  0,
                  Math.round((Number.isFinite(pounds) ? pounds : 0) * 100),
                ),
                note,
              })
            }}
            style={{ width: 140 }}
          />
        </Field>
        <Field style={{ flex: 1, minWidth: 220 }}>
          <Label>Note (optional)</Label>
          <Input
            disabled={!editable}
            maxLength={500}
            placeholder='e.g. Waste oil collection'
            value={note}
            onChange={e =>
              onChange?.({
                discountPennies,
                note: e.target.value,
              })
            }
          />
        </Field>
      </Row>
      <Breakdown>
        <span>Ordered: {formatPoundsFromPennies(orderedPennies)}</span>
        {isReceiptNotSaved(record?.receiptingStatus) ? (
          <span>Not receipted yet</span>
        ) : (
          <>
            <span>
              Receipted lines: {formatPoundsFromPennies(linesReceivedPennies)}
            </span>
            <span>Discount: −{formatPoundsFromPennies(discountPennies)}</span>
            <span>
              <strong>
                Receipted total:{' '}
                {formatPoundsFromPennies(receiptedTotalPennies)}
              </strong>
            </span>
          </>
        )}
      </Breakdown>
      {editable && isReceipted(record) ? (
        <Label>
          This order was already receipted. You can correct the discount until
          its invoice is reconciled.
        </Label>
      ) : null}
      {editable ? (
        <Actions>
          <Button
            type='primary'
            size='small'
            htmlType='button'
            loading={saving}
            disabled={!dirty || saving}
            onClick={() => onSave?.(record)}
          >
            Save discount
          </Button>
          {dirty ? (
            <Label>
              Unsaved discount — use Save discount (separate from Save receipt).
            </Label>
          ) : null}
        </Actions>
      ) : (
        <Label>
          {invoiceReconciled
            ? 'Locked because an invoice on this purchase order is reconciled.'
            : 'Locked after receipting — historical receipted value.'}
        </Label>
      )}
    </Wrap>
  )
}
